<template>
  <div class="support-menu">
    <v-menu
      v-model="menu"
      open-on-hover
      :open-delay="120"
      :close-delay="220"
      location="bottom"
      offset="8"
      transition="fade-transition"
      content-class="support-menu-overlay"
    >
      <!-- ================================================= -->
      <!-- ACTIVATOR -->
      <!-- ================================================= -->
      <template #activator="{ props }">
        <v-btn
          v-bind="props"
          variant="text"
          class="support-menu-button text-none"
          :style="{
            color:
              theme.appBarColor ||
              theme.text
          }"
        >
          <v-icon
            start
            size="20"
          >
            mdi-lifebuoy
          </v-icon>

          {{ t("support") }}

          <v-icon
            end
            size="18"
          >
            mdi-chevron-down
          </v-icon>
        </v-btn>
      </template>

      <!-- ================================================= -->
      <!-- SUPPORT CARD -->
      <!-- ================================================= -->
      <v-card
        elevation="10"
        class="support-menu-card"
        :style="{
          background:
            theme.cardBodyBg ||
            theme.surface,
          color:
            theme.cardBodyColor ||
            theme.text
        }"
      >
        <!-- =============================================== -->
        <!-- HEADER -->
        <!-- =============================================== -->
        <div class="support-header">
          <div class="support-header-icon">
            <v-icon
              size="26"
            >
              mdi-headset
            </v-icon>
          </div>

          <div>
            <div class="support-header-title">
              {{
                lang === "en"
                  ? "Get Help"
                  : "Obtenir de l'aide"
              }}
            </div>

            <div class="support-header-description">
              {{
                lang === "en"
                  ? "Find documentation or request technical assistance."
                  : "Consultez la documentation ou demandez une assistance technique."
              }}
            </div>
          </div>
        </div>

        <v-divider />

        <!-- =============================================== -->
        <!-- SUPPORT ITEMS -->
        <!-- =============================================== -->
        <div class="support-items">
          <router-link
            v-for="item in visibleSupportItems"
            :key="item.key"
            :to="item.to"
            class="support-item"
            @click="menu = false"
          >
            <div
              class="support-item-icon"
              :style="{
                background:
                  theme.sectionBgColor ||
                  'rgba(127, 127, 127, 0.10)',

                color:
                  theme.appBarBg ||
                  theme.cardBodyColor ||
                  theme.text
              }"
            >
              <v-icon
                :icon="item.icon"
                size="23"
              />
            </div>

            <div class="support-item-content">
              <div class="support-item-title">
                {{ item.title }}
              </div>

              <div class="support-item-description">
                {{ item.desc }}
              </div>
            </div>

            <v-icon
              size="18"
              class="support-item-arrow"
            >
              mdi-chevron-right
            </v-icon>
          </router-link>
        </div>

        <!-- =============================================== -->
        <!-- FOOTER -->
        <!-- =============================================== -->
        <div class="support-footer">
          <v-icon
            size="18"
          >
            mdi-clock-outline
          </v-icon>

          <span>
            {{
              lang === "en"
                ? "Technical support requests are routed to the appropriate support team."
                : "Les demandes d’assistance technique sont acheminées à l’équipe appropriée."
            }}
          </span>
        </div>
      </v-card>
    </v-menu>
  </div>

  <v-spacer />
</template>

<script setup>
import {
  computed,
  ref,
  watch
} from "vue";

import {
  useRouter
} from "vue-router";

import {
  useI18n
} from "vue-i18n";

import {
  useLangStore
} from "@/store/lang";

import {
  useAuthStore,
  useThemeStore
} from "@/store/auth";

// ============================================================
// STORES
// ============================================================

const {
  t,
  locale
} = useI18n();

const router =
  useRouter();

const langStore =
  useLangStore();

const themeStore =
  useThemeStore();

const auth =
  useAuthStore();

// ============================================================
// LANGUAGE
// ============================================================

const lang =
  computed(
    () =>
      langStore.currentLanguage ||
      "en"
  );

watch(
  lang,
  newLang => {
    locale.value =
      newLang || "en";
  },
  {
    immediate: true
  }
);

// ============================================================
// THEME
// ============================================================

const theme =
  computed(
    () =>
      themeStore.themeStyles ||
      {}
  );

// ============================================================
// MENU STATE
// ============================================================

const menu =
  ref(false);

// ============================================================
// ROUTE RESOLVER
// ============================================================

function resolveRoute(
  englishRoute,
  frenchRoute
) {
  const routeName =
    lang.value === "en"
      ? englishRoute
      : frenchRoute;

  try {
    return router.resolve({
      name:
        routeName
    }).href;
  }
  catch (error) {
    console.error(
      `Unable to resolve route "${routeName}":`,
      error
    );

    return "/";
  }
}

// ============================================================
// SUPPORT ITEMS
// ============================================================

const business =
  computed(() => [
    {
      key:
        "documentation",

      title:
        t("support"),

      desc:
        t("technicalDocumentation"),

      icon:
        "mdi-book-open-page-variant-outline",

      to:
        resolveRoute(
          "GlobalTenantHelpCenterEN",
          "GlobalTenantHelpCenterFR"
        )
    },

    {
      key:
        "technical-support",

      title:
        t("requestTechnicalSupport"),

      desc:
        t("technicalSupport"),

      icon:
        "mdi-headset",

      to:
        resolveRoute(
          "GlobalTenantHelpCenterEN",
          "GlobalTenantHelpCenterFR"
        )
    }
  ]);

// ============================================================
// PERMISSION FILTER
// ============================================================

const visibleSupportItems =
  computed(() =>
    business.value.filter(
      item => {
        if (
          !item.permission
        ) {
          return true;
        }

        return auth.hasPermission(
          item.permission
        );
      }
    )
  );
</script>

<style scoped>
/* ============================================================
   ROOT
============================================================ */

.support-menu {
  display: flex;
  align-items: center;
}

/* ============================================================
   ACTIVATOR
============================================================ */

.support-menu-button {
  min-height: 44px;
  margin-top: 20px;
  padding-right: 10px;
  padding-left: 10px;
  font-size: 16px;
  font-weight: 400;
}

/* ============================================================
   CARD
============================================================ */

.support-menu-card {
  width:
    min(
      430px,
      92vw
    );

  overflow: hidden;

  border-radius: 14px;
}

/* ============================================================
   HEADER
============================================================ */

.support-header {
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 20px 22px;
}

.support-header-icon {
  display: flex;
  width: 46px;
  height: 46px;
  flex: 0 0 46px;
  align-items: center;
  justify-content: center;
  border-radius: 11px;

  background:
    rgba(
      127,
      127,
      127,
      0.1
    );
}

.support-header-title {
  font-size: 17px;
  font-weight: 700;
}

.support-header-description {
  margin-top: 3px;
  font-size: 10.5px;
  line-height: 1.45;
  opacity: 0.62;
}

/* ============================================================
   ITEMS
============================================================ */

.support-items {
  display: flex;
  flex-direction: column;
  padding: 9px;
}

.support-item {
  display: flex;
  width: 100%;
  min-height: 70px;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 9px;
  color: inherit;
  text-decoration: none;

  transition:
    background-color 0.15s ease,
    transform 0.15s ease;
}

.support-item:hover {
  background:
    rgba(
      127,
      127,
      127,
      0.09
    );

  transform:
    translateX(2px);
}

/* ============================================================
   ITEM ICON
============================================================ */

.support-item-icon {
  display: flex;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
}

/* ============================================================
   ITEM CONTENT
============================================================ */

.support-item-content {
  min-width: 0;
  flex: 1;
}

.support-item-title {
  margin-bottom: 3px;
  font-size: 13px;
  font-weight: 650;
}

.support-item-description {
  font-size: 10.5px;
  line-height: 1.45;
  opacity: 0.62;
}

/* ============================================================
   ARROW
============================================================ */

.support-item-arrow {
  flex: 0 0 auto;
  opacity: 0.35;

  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}

.support-item:hover
.support-item-arrow {
  opacity: 1;
  transform: translateX(2px);
}

/* ============================================================
   FOOTER
============================================================ */

.support-footer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;

  border-top:
    1px solid
    rgba(
      127,
      127,
      127,
      0.15
    );

  font-size: 9.5px;
  line-height: 1.4;
  opacity: 0.62;
}

/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 599px) {
  .support-menu-card {
    width:
      min(
        360px,
        94vw
      );

    max-height: 75vh;
    overflow-y: auto;
  }

  .support-header {
    padding: 17px;
  }

  .support-item {
    min-height: 64px;
    padding: 9px 10px;
  }
}
</style>